import { SearchSource } from '@carvana/merch-contracts';
import { useEffectWithController } from '@carvana/merch-react-hooks';
import { saveSuggestionToLocalStorage } from '@carvana/merch-search-bar';
import { useCallback, useContext, useState } from 'react';
import { TEXT_SEARCH } from '~/constants/endpoints';
import AppliedFiltersContext from '~/context/common/AppliedFiltersContext';
import InventoryContext from '~/context/srp/InventoryContext';
import type { AppliedFilters } from '~/contracts/common';
import isEmpty from '~/utilities/isEmpty';
import { generateErrorLogger } from '~/utilities/loggers';
import trackOpenTextSearchResult from '~/utilities/srp/trackOpenTextSearchResult';

const logError = generateErrorLogger('useGetOpenTextSearch');

export default function useGetOpenTextSearch(): (query: string) => void {
  const { setAppliedFilters } = useContext(AppliedFiltersContext);
  const { setFreeTextNoResults } = useContext(InventoryContext);
  const [pendingSearch, setPendingSearch] = useState<{
    query: string;
  } | null>(null);

  useEffectWithController(
    controller => {
      if (pendingSearch === null) return;

      const submittedQuery = pendingSearch.query;

      async function resolve() {
        try {
          const response = await fetch(
            `${TEXT_SEARCH}?userSearchString=${encodeURIComponent(
              submittedQuery ?? ''
            )}`,
            { signal: controller.signal }
          );

          if (controller.signal.aborted) return;

          if (!response.ok) {
            throw new Error(response.statusText);
          }

          const { filters }: { filters: Partial<AppliedFilters> } =
            await response.json();

          if (controller.signal.aborted) return;

          if (isEmpty(filters)) {
            setFreeTextNoResults(submittedQuery ?? '');
            setAppliedFilters({});
            trackOpenTextSearchResult(submittedQuery ?? '', false);
          } else {
            setFreeTextNoResults(false);
            setAppliedFilters(filters);
            trackOpenTextSearchResult(submittedQuery ?? '', true);
            saveSuggestionToLocalStorage(
              // The search-bar package's own AppliedFilters type allows null values; ours doesn't.
              {
                buildKey: null,
                filters,
                highlightedText: submittedQuery ?? '',
                isSavedSuggestion: true,
                searchType: SearchSource.TextSearch,
                text: submittedQuery ?? ''
              } as Parameters<typeof saveSuggestionToLocalStorage>[0],
              logError
            );
          }
        } catch (err: unknown) {
          if (controller.signal.aborted) return;

          logError(err, {
            api: 'textsearch',
            location: 'Resolve open text search',
            operation: 'fetch',
            query: submittedQuery
          });
        }
      }

      void resolve();
    },
    [setAppliedFilters, setFreeTextNoResults, pendingSearch]
  );

  return useCallback((query: string) => {
    setPendingSearch({ query });
  }, []);
}
